import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import {
    Home, Shield, Settings, Menu, X, LogOut, Sun, Moon, Bell, ChevronRight, Power, KeyRound, BookOpen, User,
} from 'lucide-react';
import { useModuleFavicon } from '../../hooks/useModuleFavicon';
import { ChatFlotante } from './ChatFlotante';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
    { href: '/alarmas/panel', label: 'Panel', icon: Power },
    { href: '/alarmas/manual', label: 'Manual de Usuario', icon: BookOpen },
    { href: '/alarmas/admin', label: 'Admin', icon: Settings, adminOnly: true },
    { href: '/alarmas/historial', label: 'Historial', icon: Bell, adminOnly: true },
];

function useModuleTitle() {
    useEffect(() => {
        const prev = document.title;
        document.title = 'Alarmas LS - Control';
        return () => { document.title = prev; };
    }, []);
}

export function AlarmasLayout({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, refreshUser } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [pwdDialog, setPwdDialog] = useState(false);
    const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm: '' });
    const [profileDialog, setProfileDialog] = useState(false);
    const [profileForm, setProfileForm] = useState({ nombre: '', direccion: '' });
    useModuleTitle();
    useModuleFavicon('alarmas');

    const isAdmin = ['admin', 'superadmin', 'gerente'].includes(user?.rol);
    const visibleItems = navItems.filter(item => !item.adminOnly || isAdmin);

    const handleSaveProfile = async () => {
        if (!profileForm.nombre || profileForm.nombre.trim().length < 2) {
            toast.error('Nombre muy corto'); return;
        }
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/alarmas/me/profile`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ nombre: profileForm.nombre.trim(), direccion: profileForm.direccion.trim() }),
            });
            if (res.ok) {
                toast.success('Perfil actualizado');
                setProfileDialog(false);
                if (refreshUser) await refreshUser();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
            }
        } catch { toast.error('Error de conexión'); }
    };

    const handleChangePassword = async () => {
        if (!pwdForm.current_password || !pwdForm.new_password) {
            toast.error('Complete todos los campos'); return;
        }
        if (pwdForm.new_password !== pwdForm.confirm) {
            toast.error('Las contrasenas no coinciden'); return;
        }
        if (pwdForm.new_password.length < 6) {
            toast.error('Minimo 6 caracteres'); return;
        }
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/alarmas/cambiar-password`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ current_password: pwdForm.current_password, new_password: pwdForm.new_password }),
            });
            if (res.ok) {
                toast.success('Contrasena actualizada');
                setPwdDialog(false);
                setPwdForm({ current_password: '', new_password: '', confirm: '' });
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
            }
        } catch { toast.error('Error'); }
    };

    return (
        <div className="min-h-screen bg-background" style={{ '--alarmas-primary': '#ef4444', '--alarmas-secondary': '#1a1a1a' }}>
            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 bg-card border-b-2">
                <div className="flex items-center justify-between h-full px-4">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
                            <Menu className="h-5 w-5" />
                        </Button>
                        <Shield className="h-6 w-6 text-red-500" />
                        <span className="font-black text-lg uppercase">Alarmas</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={toggleTheme}>
                            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { logout(); navigate('/login'); }} className="text-destructive">
                            <LogOut className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            </header>

            {sidebarOpen && <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setSidebarOpen(false)} />}

            {/* Sidebar */}
            <aside className={cn(
                "fixed top-0 left-0 z-50 h-full w-64 bg-card border-r-2 transition-transform duration-300 lg:translate-x-0",
                sidebarOpen ? "translate-x-0" : "-translate-x-full"
            )}>
                <div className="flex flex-col h-full">
                    <div className="h-16 flex items-center justify-between px-4 border-b">
                        <Link to="/alarmas/panel" className="flex items-center gap-2">
                            <Shield className="h-6 w-6 text-red-500" />
                            <span className="font-black text-lg uppercase">Alarmas_LS</span>
                        </Link>
                        <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                            <X className="h-5 w-5" />
                        </Button>
                    </div>

                    <nav className="flex-1 py-4 px-3 space-y-1">
                        {visibleItems.map(item => {
                            const isActive = location.pathname === item.href;
                            const Icon = item.icon;
                            return (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    onClick={() => setSidebarOpen(false)}
                                    className={cn(
                                        "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
                                        isActive ? "bg-red-500 text-white font-bold" : "text-muted-foreground hover:bg-muted"
                                    )}
                                >
                                    <Icon className="h-5 w-5" />
                                    <span>{item.label}</span>
                                    {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="p-4 border-t">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-9 h-9 rounded-full bg-red-500/20 flex items-center justify-center">
                                <span className="text-sm font-bold text-red-500">{user?.nombre?.charAt(0)}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium truncate text-sm">{user?.nombre}</p>
                                <p className="text-xs text-muted-foreground truncate">{user?.rol}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate('/home')}>
                                <Home className="h-4 w-4 mr-1" /> Inicio
                            </Button>
                            <Button variant="outline" size="icon" onClick={() => {
                                setProfileForm({ nombre: user?.nombre || '', direccion: user?.direccion || '' });
                                setProfileDialog(true);
                            }} title="Editar mi perfil" data-testid="btn-edit-profile">
                                <User className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="icon" onClick={() => setPwdDialog(true)} title="Cambiar contrasena">
                                <KeyRound className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="icon" onClick={toggleTheme} title={isDark ? 'Tema claro' : 'Tema oscuro'}>
                                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                            </Button>
                            <Button variant="outline" size="icon" onClick={() => { logout(); navigate('/login'); }} title="Cerrar sesión" className="text-destructive hover:text-destructive" data-testid="btn-logout-desktop">
                                <LogOut className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </aside>

            <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
                <div className="p-4 lg:p-6">{children}</div>
            </main>
            <ChatFlotante />

            {/* Dialog Editar Mi Perfil */}
            <Dialog open={profileDialog} onOpenChange={setProfileDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><User className="h-5 w-5" /> Mi Perfil</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nombre completo</Label>
                            <Input value={profileForm.nombre} onChange={e => setProfileForm({...profileForm, nombre: e.target.value})} placeholder="Tu nombre" data-testid="input-profile-nombre" />
                        </div>
                        <div className="space-y-2">
                            <Label>Calle y número</Label>
                            <Input value={profileForm.direccion} onChange={e => setProfileForm({...profileForm, direccion: e.target.value})} placeholder="Ej: Murillo 1467" data-testid="input-profile-direccion" />
                            <p className="text-xs text-muted-foreground">Aparecerá en las alertas que envíes a tus vecinos.</p>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            Email: <strong>{user?.email}</strong> (no editable. Pídele al administrador cambiarlo)
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setProfileDialog(false)}>Cancelar</Button>
                        <Button onClick={handleSaveProfile} data-testid="btn-save-profile">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Cambiar Contraseña */}
            <Dialog open={pwdDialog} onOpenChange={setPwdDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Cambiar Contrasena</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Contrasena Actual</Label>
                            <Input type="password" value={pwdForm.current_password} onChange={e => setPwdForm({...pwdForm, current_password: e.target.value})} />
                        </div>
                        <div className="space-y-2">
                            <Label>Nueva Contrasena</Label>
                            <Input type="password" value={pwdForm.new_password} onChange={e => setPwdForm({...pwdForm, new_password: e.target.value})} />
                        </div>
                        <div className="space-y-2">
                            <Label>Confirmar Nueva Contrasena</Label>
                            <Input type="password" value={pwdForm.confirm} onChange={e => setPwdForm({...pwdForm, confirm: e.target.value})} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPwdDialog(false)}>Cancelar</Button>
                        <Button onClick={handleChangePassword}>Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
